/**
 * Whether an account may open a screen, and nothing else.
 *
 * # Why it is its own file, and why it imports nothing
 *
 * Three readers ask this question — the sidebar, the landing rule and the router's
 * guard — and until 2026-09-20 all three asked it through a function that reached for a
 * Pinia store. A rule that can only be evaluated inside a running browser is a rule that
 * can only be checked by opening the console and signing in as somebody, which is how a
 * console shipped a redirect onto `/porocila/:id/teki/:runId` and nobody noticed until a
 * reviewer held an account with one right.
 *
 * So the rule takes what it needs as an ARGUMENT: `holds` is „does this account hold this
 * key“, and where it comes from — a store, a test's Set, a list out of a fixture — is not
 * this file's business. `tools/landing.test.mjs` walks all 128 subsets of the seven
 * `group:*` rights through it in a few milliseconds, which is a thing no browser test
 * would ever be asked to do.
 *
 * Nothing here is allowed to import the router, a store or a component. That is the whole
 * property, and it is worth more than the four lines it saves elsewhere.
 */

/**
 * What a screen declares it needs.
 *
 * The three forms are the vocabulary the reception portal already uses
 * (`pms-web/src/types/Nav.ts`), so that the two consoles are read the same way:
 *
 *   - `permission` — the one key the menu entry is drawn from, and the narrowest honest
 *     declaration where the service's own door is an OR (the notes in `router/index.ts`
 *     name the four screens that pay for this);
 *   - `anyOf`      — held where ANY of them opens the screen. No screen of this console
 *     carries it yet; it is here because the router's own comments have asked for a
 *     permission LIST four times, and the day one arrives the rule already knows the word;
 *   - `allOf`      — every key the screen's FIRST reads need. It is an AND and never an
 *     OR: „Knjigovodstvo“ needs its own right AND `group:read` for the register of
 *     companies its picker hangs off, and an account holding one of the two met a 403 on
 *     the first paint.
 *
 * A gate carrying several forms is the narrower answer — all of them must pass. A menu
 * entry that leads to a refusal is worse than no entry at all.
 */
export interface INavGate {
    permission?: string
    anyOf?:      string[]
    allOf?:      string[]
}

/**
 * Whether an account holding `holds` may open a screen declaring `gate`.
 *
 * A gate that declares nothing opens for everybody, which is deliberate and is the one
 * case worth naming: „Brez dostopa“ declares nothing because it is the sentence an
 * account with no group right at all is shown. It is kept out of the LANDING search by
 * not being in the navigation manifest rather than by a flag here — see `navigation.ts`.
 */
export const mayOpenGate = (gate: INavGate, holds: (key: string) => boolean): boolean => {
    if (gate.permission && !holds(gate.permission)) return false

    if (gate.anyOf?.length && !gate.anyOf.some(key => holds(key))) return false

    return (gate.allOf ?? []).every(key => holds(key))
}
